import React from 'react';
import { ActiveCustomer, AgentCustomerAnalysis } from '../../types/organizations';

interface CustomerReasoningModalProps {
  customer: ActiveCustomer;
  analysis: AgentCustomerAnalysis | null;
  isOpen: boolean;
  onClose: () => void;
}

// Helper function to get category styling
const getCategoryStyling = (category: string): { bg: string; text: string } => {
  const categoryLower = category.toLowerCase();
  
  if (categoryLower.includes('regular') || categoryLower.includes('standard')) {
    return { bg: 'bg-blue-100', text: 'text-blue-800' };
  } else if (categoryLower.includes('premium') || categoryLower.includes('vip')) {
    return { bg: 'bg-purple-100', text: 'text-purple-800' };
  } else if (categoryLower.includes('at-risk') || categoryLower.includes('risk')) {
    return { bg: 'bg-red-100', text: 'text-red-800' };
  } else if (categoryLower.includes('new') || categoryLower.includes('prospect')) {
    return { bg: 'bg-green-100', text: 'text-green-800' };
  } else if (categoryLower.includes('inactive') || categoryLower.includes('dormant')) {
    return { bg: 'bg-gray-100', text: 'text-gray-800' };
  } else {
    return { bg: 'bg-indigo-100', text: 'text-indigo-800' };
  }
};

const CustomerReasoningModal: React.FC<CustomerReasoningModalProps> = ({ customer, analysis, isOpen, onClose }) => {
  if (!isOpen || !analysis) return null;

  const profileSummary = analysis?.analysis_type === 'customer_insight'
    ? (analysis?.output_json?.customer_profile_summary || 'No profile summary available')
    : undefined;

  const selectedCategory = analysis?.analysis_type === 'customer_insight'
    ? (analysis?.output_json?.selected_category || 'Unknown Category')
    : undefined;

  const score = ((): number | null => {
    if (analysis?.analysis_type === 'payment_reliability_score' || analysis?.analysis_type === 'financial_risk_score') {
      const raw = analysis?.output_json?.score;
      if (raw === undefined || raw === null) return null;
      return typeof raw === 'string' ? (parseInt(raw) || 0) : raw;
    }
    return null;
  })();

  const reasonings = analysis?.reasonings || [];

  const headerChip = ((): React.ReactElement | null => {
    if (analysis?.analysis_type === 'customer_insight' && selectedCategory) {
      return (
        <div className={`px-3 py-1 ${getCategoryStyling(selectedCategory).bg} ${getCategoryStyling(selectedCategory).text} text-sm font-medium rounded-full`}>
          {selectedCategory.trim()}
        </div>
      );
    }
    if (analysis?.analysis_type === 'payment_reliability_score' && score !== null) {
      return (
        <div className={`px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full`}>
          Reliability: {score}/10
        </div>
      );
    }
    if (analysis?.analysis_type === 'financial_risk_score' && score !== null) {
      return (
        <div className={`px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full`}>
          Finance Risk: {score}/10
        </div>
      );
    }
    return null;
  })();

  const reasoningsTitle = ((): string => {
    if (analysis?.analysis_type === 'payment_reliability_score') return 'Reliability Reasonings';
    if (analysis?.analysis_type === 'financial_risk_score') return 'Finance Risk Reasonings';
    return 'Category Reasonings';
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center space-x-3">
              <h3 className="text-xl font-bold text-gray-900">{customer.name}</h3>
              {headerChip}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Profile Summary (only for customer_insight) */}
          {profileSummary && (
            <div className="mb-3">
              <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-lg">
                {profileSummary}
              </p>
            </div>
          )}

          {/* Financial Chips */}
          <div className="mb-3">
            <div className="flex flex-wrap gap-2">
              {customer.invoice_metadata?.outstanding_invoice_count !== undefined && customer.invoice_metadata?.last_12m_invoice_count !== undefined && (
                <span className="text-xs bg-blue-50 text-blue-800 px-3 py-2 rounded-full">
                  LTM Invoices: {customer.invoice_metadata.outstanding_invoice_count || 0}/{customer.invoice_metadata.last_12m_invoice_count || 0}
                </span>
              )}
              {customer.invoice_metadata?.total_receivable !== undefined && (
                <span className="text-xs bg-green-50 text-green-800 px-3 py-2 rounded-full">
                  Receivable: ${(customer.invoice_metadata.total_receivable || 0).toLocaleString()}
                </span>
              )}
              {customer.invoice_metadata?.last_12m_value && (
                <span className="text-xs bg-purple-50 text-purple-800 px-3 py-2 rounded-full">
                  LTM Value: ${customer.invoice_metadata.last_12m_value.toLocaleString()}
                </span>
              )}
            </div>
          </div>

          {/* Reasonings */}
          <div>
            <h5 className="font-medium text-gray-700 mb-3">{reasoningsTitle}</h5>
            {reasonings.length > 0 ? (
              <div className="space-y-3">
                {reasonings.map((reasoning: any, index: number) => {
                  // Handle both string and object reasonings
                  if (typeof reasoning === 'string') {
                    return (
                      <div key={index} className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                        <p className="text-sm text-blue-800">{reasoning}</p>
                      </div>
                    );
                  } else if (typeof reasoning === 'object' && reasoning !== null) {
                    // Handle object reasonings by rendering each key-value pair
                    return (
                      <div key={index} className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                        {Object.entries(reasoning).map(([key, value]) => (
                          <div key={key} className="mb-2 last:mb-0">
                            <h6 className="font-medium text-blue-900 text-sm mb-1">{key}:</h6>
                            <div className="text-sm text-blue-800 ml-2">
                              {typeof value === 'object' && value !== null ? (
                                // Handle nested objects (like criteria)
                                <div className="ml-2">
                                  {Object.entries(value).map(([nestedKey, nestedValue]) => (
                                    <div key={nestedKey} className="mb-1">
                                      <span className="font-medium text-blue-800">{nestedKey}:</span>
                                      <span className="ml-1">{String(nestedValue)}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span>{String(value)}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  } else {
                    // Fallback for any other type
                    return (
                      <div key={index} className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                        <p className="text-sm text-blue-800">{String(reasoning)}</p>
                      </div>
                    );
                  }
                })}
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-sm text-gray-500">No detailed reasonings available for this analysis.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerReasoningModal;
